import { FC, Fragment, useEffect, useState } from "react";
import { Combobox, Transition } from "@headlessui/react";
import { useQuery } from "@apollo/client";
import { STATES_QUERY } from "../../../modules/Services/Queries/Common/CommonQueries";
import { State } from "../../../types/State";

const STATE_ABBREVIATIONS: Record<string, string> = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
  DC: "District of Columbia",
};

const normalizeStateSearchValue = (value: string) =>
  value.toLowerCase().replace(/[^a-z]/g, "");

const getStateAbbreviation = (stateName: string) =>
  Object.entries(STATE_ABBREVIATIONS).find(
    ([, name]) =>
      normalizeStateSearchValue(name) === normalizeStateSearchValue(stateName),
  )?.[0] || "";

const StateBox: FC<{
  value: string;
  onChange: (value: string) => void;
  isIncorrectValue?: boolean;
  labelWidth?: string;
  inputWidth?: string;
  disabled?: boolean;
  onChangeTax?: (value: null | number) => void;
}> = ({
  value,
  onChange,
  isIncorrectValue,
  labelWidth,
  inputWidth,
  disabled,
  onChangeTax,
}) => {
  const { data: client, loading: clientLoading } = useQuery(STATES_QUERY, {
    notifyOnNetworkStatusChange: true,
  });
  const [stateList, setStateList] = useState<State[]>([]);
  const [filteredStateList, setFilteredStateList] = useState<State[]>([]);
  const [query, setQuery] = useState<string>("");
  const [displayedValue, setDisplayedValue] = useState<string>("");

  const getStateForInput = (inputValue: string) => {
    const normalizedInput = normalizeStateSearchValue(inputValue);
    const abbreviationMatch = STATE_ABBREVIATIONS[inputValue.trim().toUpperCase()];

    return stateList.find((state) => {
      const normalizedName = normalizeStateSearchValue(state.name);

      return (
        normalizedName === normalizedInput ||
        normalizeStateSearchValue(abbreviationMatch || "") === normalizedName
      );
    });
  };

  useEffect(() => {
    if (client) {
      setStateList(client.states);
    }
  }, [client]);

  useEffect(() => {
    setFilteredStateList(
      stateList.filter((state) => {
        if (query) {
          const normalizedQuery = normalizeStateSearchValue(query);
          const stateAbbreviation = getStateAbbreviation(state.name).toLowerCase();

          return (
            normalizeStateSearchValue(state.name).includes(normalizedQuery) ||
            stateAbbreviation.startsWith(normalizedQuery)
          );
        }
        return stateList;
      }),
    );
  }, [stateList, query]);

  const onBeforeLeaveOptionsListHandler = () => {
    const matchedState = getStateForInput(query);

    if (matchedState) {
      onChange(matchedState.name);
      return;
    }

    if (query && !filteredStateList.length) {
      onChange(query);
    }

    if (filteredStateList.length === 1) {
      onChange(filteredStateList[0].name);
      return;
    }

    if (displayedValue) {
      onChange(displayedValue);
    }

    if (!query) {
      onChange("");
    }
  };

  useEffect(() => {
    if (clientLoading || (value && !stateList.length)) return;

    const selectedValue = stateList.find((state) => state.name === value);

    if (onChangeTax) {
      onChangeTax(selectedValue?.tax || null);
    }
  }, [value, stateList, clientLoading]);

  return (
    <div className="state-box">
      <div
        className="state-box__label"
        style={{ width: `${labelWidth || "max-content"}` }}
      >
        State {isIncorrectValue && <i className="icon-cancel-circled red" />}
      </div>
      <div style={{ width: `${inputWidth || "unset"}` }}>
        <Combobox
          value={value}
          onChange={(stateName) => onChange(stateName)}
          disabled={disabled}
        >
          <div className="state-box__wrapper">
            <Combobox.Input
              onChange={(event) => {
                const value = event.currentTarget.value;
                setQuery(value);
              }}
              displayValue={() => displayedValue || value || query || ""}
              className={`state-box__input ${
                isIncorrectValue ? "state-box__input_error" : ""
              }`}
            />
            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              beforeLeave={onBeforeLeaveOptionsListHandler}
              afterLeave={() => setQuery("")}
            >
              <Combobox.Options static className="state-box__options">
                {clientLoading && (
                  <Combobox.Option
                    disabled
                    value="loading"
                    className="state-box__option"
                  >
                    Loading
                  </Combobox.Option>
                )}
                {!clientLoading && !filteredStateList.length && query && (
                  <Combobox.Option
                    disabled
                    value="no-data"
                    className="state-box__option"
                  >
                    Nothing found.
                  </Combobox.Option>
                )}
                {!clientLoading &&
                  filteredStateList.map((state) => (
                    <Combobox.Option
                      key={state.id}
                      onMouseEnter={() => {
                        setDisplayedValue(state.name);
                      }}
                      value={state.name}
                      className="state-box__option"
                    >
                      <span className="font-bold">{state.name}</span>
                    </Combobox.Option>
                  ))}
              </Combobox.Options>
            </Transition>
          </div>
        </Combobox>
      </div>
    </div>
  );
};

export default StateBox;
